import { Badge, Radio } from "antd";
import cn from "classnames";
import { defineMessages, useIntl, MessageDescriptor } from "react-intl";

import { IChoice, IScaleDetail, LanguagesIsoEnum } from "@/modules/assessments/types/test.interface";

import { QuestionTypes, TestElementMode } from "@/shared/components/candidate/scenes/TestElement/TestElement";
import { getScaleRepresentation, getUniqueKeysValues } from "@/shared/components/testManagement/TestElementInfoBlock";

import badgeStyle from "@/shared/components/assessment/report/TestAnalysisBlock/style.module.css";
import style from "./style.module.css";

const answerLabelMessages = defineMessages({
  trueFalseTrue: { id: "candidate.answer.trueFalse.true", defaultMessage: "yes" },
  trueFalseFalse: { id: "candidate.answer.trueFalse.false", defaultMessage: "no" },
  followTrue: { id: "candidate.answer.followNotFollow.true", defaultMessage: "follows" },
  followFalse: { id: "candidate.answer.followNotFollow.false", defaultMessage: "does not follow" },
  correctTrue: { id: "candidate.answer.correctIncorrect.true", defaultMessage: "correct" },
  correctFalse: { id: "candidate.answer.correctIncorrect.false", defaultMessage: "incorrect" },
  madeTrue: { id: "candidate.answer.madeNotMade.true", defaultMessage: "made" },
  madeFalse: { id: "candidate.answer.madeNotMade.false", defaultMessage: "not made" },
  strongTrue: { id: "candidate.answer.strongWeak.true", defaultMessage: "strong" },
  strongFalse: { id: "candidate.answer.strongWeak.false", defaultMessage: "weak" },
});

function getAnswerLabelMessages(questionType: QuestionTypes): { true: MessageDescriptor; false: MessageDescriptor } {
  switch (questionType) {
    case QuestionTypes.FollowNotFollow:
      return { true: answerLabelMessages.followTrue, false: answerLabelMessages.followFalse };
    case QuestionTypes.CorrectIncorrect:
      return { true: answerLabelMessages.correctTrue, false: answerLabelMessages.correctFalse };
    case QuestionTypes.MadeNotMade:
      return { true: answerLabelMessages.madeTrue, false: answerLabelMessages.madeFalse };
    case QuestionTypes.StrongWeak:
      return { true: answerLabelMessages.strongTrue, false: answerLabelMessages.strongFalse };
    case QuestionTypes.TrueFalse:
    default:
      return { true: answerLabelMessages.trueFalseTrue, false: answerLabelMessages.trueFalseFalse };
  }
}

export function getEnglishAnswerLabels() {
  const enUS = (descriptor: MessageDescriptor) => ({ [LanguagesIsoEnum.enUS]: descriptor.defaultMessage as string });
  const trueLabel = (questionType: QuestionTypes) => enUS(getAnswerLabelMessages(questionType).true);
  const falseLabel = (questionType: QuestionTypes) => enUS(getAnswerLabelMessages(questionType).false);
  return {
    true: {
      [QuestionTypes.TrueFalse]: trueLabel(QuestionTypes.TrueFalse),
      [QuestionTypes.FollowNotFollow]: trueLabel(QuestionTypes.FollowNotFollow),
      [QuestionTypes.CorrectIncorrect]: trueLabel(QuestionTypes.CorrectIncorrect),
      [QuestionTypes.MadeNotMade]: trueLabel(QuestionTypes.MadeNotMade),
      [QuestionTypes.StrongWeak]: trueLabel(QuestionTypes.StrongWeak),
    },
    false: {
      [QuestionTypes.TrueFalse]: falseLabel(QuestionTypes.TrueFalse),
      [QuestionTypes.FollowNotFollow]: falseLabel(QuestionTypes.FollowNotFollow),
      [QuestionTypes.CorrectIncorrect]: falseLabel(QuestionTypes.CorrectIncorrect),
      [QuestionTypes.MadeNotMade]: falseLabel(QuestionTypes.MadeNotMade),
      [QuestionTypes.StrongWeak]: falseLabel(QuestionTypes.StrongWeak),
    },
  };
}

interface ITrueFalseRadioGroup {
  id: string;
  mode?: TestElementMode;
  choices?: IChoice[];
  onRadioChange: (e: any) => void;
  currentAnswer: boolean | string | undefined;
  questionType:
    | QuestionTypes.TrueFalse
    | QuestionTypes.FollowNotFollow
    | QuestionTypes.CorrectIncorrect
    | QuestionTypes.MadeNotMade
    | QuestionTypes.StrongWeak;
  trueKey: string | undefined;
  falseKey: string | undefined;
  disabled?: boolean;
}

const positiveBadgeColor = "#66CC99";
const negativeBadgeColor = "#c35445";
const mixedBadgeColor = "#f6b444";

export const getScaleBadgeColor = (scaleRepresentation: string): string => {
  const hasPositiveScores = scaleRepresentation.includes("+");
  const hasNegativeScores = scaleRepresentation.includes("-");

  if (hasPositiveScores && !hasNegativeScores) return positiveBadgeColor;
  if (hasNegativeScores && !hasPositiveScores) return negativeBadgeColor;

  return mixedBadgeColor;
};

export default function TrueFalseRadioGroup({
  id,
  mode = TestElementMode.Candidate,
  choices,
  onRadioChange,
  currentAnswer,
  questionType,
  trueKey,
  falseKey,
  disabled = false,
}: ITrueFalseRadioGroup) {
  const intl = useIntl();
  const answerLabels = getAnswerLabelMessages(questionType);
  const isAdminView = mode === TestElementMode.Admin;
  const showBadge = isAdminView;
  const trueLabel = isAdminView
    ? getEnglishAnswerLabels().true[questionType][LanguagesIsoEnum.enUS]
    : intl.formatMessage(answerLabels.true);
  const falseLabel = isAdminView
    ? getEnglishAnswerLabels().false[questionType][LanguagesIsoEnum.enUS]
    : intl.formatMessage(answerLabels.false);

  const trueScales: (string | IScaleDetail)[] | undefined = choices?.filter((choice) => choice.choice === true)[0]
    .scales;
  const falseScales: (string | IScaleDetail)[] | undefined = choices?.filter((choice) => choice.choice === false)[0]
    .scales;

  const trueScaleValue = trueScales ? getUniqueKeysValues([trueScales]) : {};
  const falseScaleValue = falseScales ? getUniqueKeysValues([falseScales]) : {};
  const trueScalePoints = getScaleRepresentation(trueScaleValue);
  const falseScalePoints = getScaleRepresentation(falseScaleValue);

  return (
    <Radio.Group
      size={"large"}
      id={String(id)}
      name={String(id)}
      value={currentAnswer}
      onChange={onRadioChange}
      disabled={disabled}
      className={cn(style.answersContainer, { [style.analysis]: showBadge })}
    >
      <Badge
        count={(showBadge && trueScalePoints) || 0}
        offset={[2, -2]}
        color={getScaleBadgeColor(trueScalePoints)}
        className={badgeStyle.badge}
      >
        <Radio value={true} id={trueKey}>
          <p>{trueLabel}</p>
        </Radio>
      </Badge>
      <Badge
        count={(showBadge && falseScalePoints) || 0}
        offset={[2, -2]}
        color={getScaleBadgeColor(falseScalePoints)}
        className={badgeStyle.badge}
      >
        <Radio value={false} id={falseKey}>
          <p>{falseLabel}</p>
        </Radio>
      </Badge>
    </Radio.Group>
  );
}
